import React, { useState, useEffect, useRef } from 'react';
import { Institution, InstitutionActivity, InstitutionHolding, InstitutionSector } from '../types';
import { apiService } from '../services/apiService';

type ViewType = 'list' | 'activity' | 'holdings' | 'sectors';
type SortField = 'name' | 'total_value' | 'share_value' | 'is_hedge_fund' | 'filing_date';
type SortDirection = 'asc' | 'desc';

export const Institutions: React.FC = () => {
  // Refs for synchronized scrolling - Main list
  const topScrollRef = useRef<HTMLDivElement>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const topScrollContentRef = useRef<HTMLDivElement>(null);
  
  // Refs for synchronized scrolling - Holdings
  const holdingsTopScrollRef = useRef<HTMLDivElement>(null);
  const holdingsTableContainerRef = useRef<HTMLDivElement>(null);
  const holdingsTopScrollContentRef = useRef<HTMLDivElement>(null);
  
  // Refs for synchronized scrolling - Sectors
  const sectorsTopScrollRef = useRef<HTMLDivElement>(null);
  const sectorsTableContainerRef = useRef<HTMLDivElement>(null);
  const sectorsTopScrollContentRef = useRef<HTMLDivElement>(null);
  
  // Refs for synchronized scrolling - Activity
  const activityTopScrollRef = useRef<HTMLDivElement>(null);
  const activityTableContainerRef = useRef<HTMLDivElement>(null);
  const activityTopScrollContentRef = useRef<HTMLDivElement>(null);
  
  // Main list state
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<SortField>('total_value');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  
  // Filter states
  const [minTotalValue, setMinTotalValue] = useState('');
  const [maxTotalValue, setMaxTotalValue] = useState('');
  const [hedgeFundFilter, setHedgeFundFilter] = useState<'all' | 'hedge_fund' | 'other'>('all');
  
  // Child view state
  const [currentView, setCurrentView] = useState<ViewType>('list');
  const [selectedInstitution, setSelectedInstitution] = useState<Institution | null>(null);
  const [activityData, setActivityData] = useState<InstitutionActivity[]>([]);
  const [holdingsData, setHoldingsData] = useState<InstitutionHolding[]>([]);
  const [sectorsData, setSectorsData] = useState<InstitutionSector[]>([]);
  const [childLoading, setChildLoading] = useState(false);

  // Child view pagination/search/sort
  const [childSearchTerm, setChildSearchTerm] = useState('');
  const [childCurrentPage, setChildCurrentPage] = useState(1);
  const [childItemsPerPage] = useState(50);
  const [childSortField, setChildSortField] = useState<string>('');
  const [childSortDirection, setChildSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    loadInstitutions();
  }, []);

  // Synchronize top and bottom scrollbars - Main list
  useEffect(() => {
    if (currentView !== 'list' || loading) return;
    
    const topScroll = topScrollRef.current;
    const tableContainer = tableContainerRef.current;
    const topScrollContent = topScrollContentRef.current;

    if (!topScroll || !tableContainer || !topScrollContent) return;

    // Sync table width to top scrollbar
    const syncWidth = () => {
      const table = tableContainer.querySelector('table');
      if (table) {
        topScrollContent.style.width = `${table.scrollWidth}px`;
      }
    };

    // Sync scrolling from top scrollbar to table
    const handleTopScroll = () => {
      if (tableContainer && topScroll) {
        tableContainer.scrollLeft = topScroll.scrollLeft;
      }
    };

    // Sync scrolling from table to top scrollbar
    const handleTableScroll = () => {
      if (topScroll && tableContainer) {
        topScroll.scrollLeft = tableContainer.scrollLeft;
      }
    };

    // Multiple sync attempts to ensure width is set
    const timeoutId1 = setTimeout(() => syncWidth(), 50);
    const timeoutId2 = setTimeout(() => syncWidth(), 200);
    const timeoutId3 = setTimeout(() => syncWidth(), 500);

    // Sync width on window resize or data change
    const resizeObserver = new ResizeObserver(syncWidth);
    const table = tableContainer.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    // Add scroll event listeners
    topScroll.addEventListener('scroll', handleTopScroll);
    tableContainer.addEventListener('scroll', handleTableScroll);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableContainer.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [institutions, currentView, loading]);

  // Synchronize top and bottom scrollbars - Holdings
  useEffect(() => {
    if (currentView !== 'holdings' || childLoading) return;
    
    const topScroll = holdingsTopScrollRef.current;
    const tableContainer = holdingsTableContainerRef.current;
    const topScrollContent = holdingsTopScrollContentRef.current;

    if (!topScroll || !tableContainer || !topScrollContent) return;

    const syncWidth = () => {
      const table = tableContainer.querySelector('table');
      if (table) {
        topScrollContent.style.width = `${table.scrollWidth}px`;
      }
    };

    const handleTopScroll = () => {
      if (tableContainer && topScroll) {
        tableContainer.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      if (topScroll && tableContainer) {
        topScroll.scrollLeft = tableContainer.scrollLeft;
      }
    };

    // Multiple sync attempts to ensure width is set
    const timeoutId1 = setTimeout(() => syncWidth(), 50);
    const timeoutId2 = setTimeout(() => syncWidth(), 200);
    const timeoutId3 = setTimeout(() => syncWidth(), 500);
    
    const resizeObserver = new ResizeObserver(syncWidth);
    const table = tableContainer.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    topScroll.addEventListener('scroll', handleTopScroll);
    tableContainer.addEventListener('scroll', handleTableScroll);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableContainer.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [holdingsData, currentView, childLoading]);

  // Synchronize top and bottom scrollbars - Sectors
  useEffect(() => {
    if (currentView !== 'sectors' || childLoading) return;
    
    const topScroll = sectorsTopScrollRef.current;
    const tableContainer = sectorsTableContainerRef.current;
    const topScrollContent = sectorsTopScrollContentRef.current;

    if (!topScroll || !tableContainer || !topScrollContent) return;

    const syncWidth = () => {
      const table = tableContainer.querySelector('table');
      if (table) {
        topScrollContent.style.width = `${table.scrollWidth}px`;
      }
    };

    const handleTopScroll = () => {
      if (tableContainer && topScroll) {
        tableContainer.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      if (topScroll && tableContainer) {
        topScroll.scrollLeft = tableContainer.scrollLeft;
      }
    };

    // Multiple sync attempts to ensure width is set
    const timeoutId1 = setTimeout(() => syncWidth(), 50);
    const timeoutId2 = setTimeout(() => syncWidth(), 200);
    const timeoutId3 = setTimeout(() => syncWidth(), 500);
    
    const resizeObserver = new ResizeObserver(syncWidth);
    const table = tableContainer.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    topScroll.addEventListener('scroll', handleTopScroll);
    tableContainer.addEventListener('scroll', handleTableScroll);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableContainer.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [sectorsData, currentView, childLoading]);

  // Synchronize top and bottom scrollbars - Activity
  useEffect(() => {
    if (currentView !== 'activity' || childLoading) return;
    
    const topScroll = activityTopScrollRef.current;
    const tableContainer = activityTableContainerRef.current;
    const topScrollContent = activityTopScrollContentRef.current;

    if (!topScroll || !tableContainer || !topScrollContent) return;

    const syncWidth = () => {
      const table = tableContainer.querySelector('table');
      if (table) {
        topScrollContent.style.width = `${table.scrollWidth}px`;
      }
    };

    const handleTopScroll = () => {
      if (tableContainer && topScroll) {
        tableContainer.scrollLeft = topScroll.scrollLeft;
      }
    };

    const handleTableScroll = () => {
      if (topScroll && tableContainer) {
        topScroll.scrollLeft = tableContainer.scrollLeft;
      }
    };

    // Multiple sync attempts to ensure width is set
    const timeoutId1 = setTimeout(() => syncWidth(), 50);
    const timeoutId2 = setTimeout(() => syncWidth(), 200);
    const timeoutId3 = setTimeout(() => syncWidth(), 500);
    
    const resizeObserver = new ResizeObserver(syncWidth);
    const table = tableContainer.querySelector('table');
    if (table) {
      resizeObserver.observe(table);
    }

    topScroll.addEventListener('scroll', handleTopScroll);
    tableContainer.addEventListener('scroll', handleTableScroll);

    return () => {
      clearTimeout(timeoutId1);
      clearTimeout(timeoutId2);
      clearTimeout(timeoutId3);
      topScroll.removeEventListener('scroll', handleTopScroll);
      tableContainer.removeEventListener('scroll', handleTableScroll);
      resizeObserver.disconnect();
    };
  }, [activityData, currentView, childLoading]);

  const loadInstitutions = async () => {
    try {
      setLoading(true);
      const data = await apiService.getInstitutions({ limit: 500 });
      setInstitutions(data);
    } catch (error) {
      console.error('Failed to load institutions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field: string, currentField: string, direction: SortDirection) => {
    if (currentField !== field) return '↕️';
    return direction === 'asc' ? '↑' : '↓';
  };

  const formatValue = (value: string | number | null | undefined) => {
    // Handle null, undefined, or empty string
    if (value === null || value === undefined || value === '') return 'N/A';
    
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || !isFinite(numValue)) return 'N/A';
    
    if (numValue >= 1000000000000) {
      return `$${(numValue / 1000000000000).toFixed(2)}T`;
    } else if (numValue >= 1000000000) {
      return `$${(numValue / 1000000000).toFixed(2)}B`;
    } else if (numValue >= 1000000) {
      return `$${(numValue / 1000000).toFixed(2)}M`;
    } else if (numValue >= 1000) {
      return `$${(numValue / 1000).toFixed(0)}K`;
    }
    return `$${numValue.toFixed(2)}`;
  };

  const formatPercent = (value: string | number | null | undefined) => {
    if (value === null || value === undefined || value === '') return 'N/A';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue) || !isFinite(numValue)) return 'N/A';
    return `${(numValue * 100).toFixed(2)}%`;
  };

  // Filter institutions
  const filteredInstitutions = institutions.filter(inst => {
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      if (
        !inst.name.toLowerCase().includes(search) &&
        !inst.short_name?.toLowerCase().includes(search) &&
        !inst.cik.includes(search)
      ) {
        return false;
      }
    }
    
    // Hedge fund filter
    if (hedgeFundFilter === 'hedge_fund' && !inst.is_hedge_fund) return false;
    if (hedgeFundFilter === 'other' && inst.is_hedge_fund) return false;
    
    // Total value filter
    const totalValue = inst.total_value ? parseFloat(inst.total_value) : 0;
    if (minTotalValue && !isNaN(totalValue) && totalValue < parseFloat(minTotalValue)) return false;
    if (maxTotalValue && !isNaN(totalValue) && totalValue > parseFloat(maxTotalValue)) return false;
    
    return true;
  });

  // Sort institutions
  const sortedInstitutions = [...filteredInstitutions].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle null/undefined values
    if (aValue === null || aValue === undefined) aValue = '';
    if (bValue === null || bValue === undefined) bValue = '';

    if (['total_value', 'share_value'].includes(sortField)) {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (sortField === 'filing_date') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    } else if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = typeof bValue === 'string' ? bValue.toLowerCase() : '';
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination
  const totalPages = Math.ceil(sortedInstitutions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstitutions = sortedInstitutions.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Child view handlers
  const handleViewActivity = async (institution: Institution) => {
    setSelectedInstitution(institution);
    setCurrentView('activity');
    setChildLoading(true);
    setChildSearchTerm('');
    setChildCurrentPage(1);
    setChildSortField('report_date');
    try {
      const data = await apiService.getInstitutionActivity(institution.name, { limit: 500 });
      setActivityData(data);
    } catch (error) {
      console.error('Failed to load activity:', error);
      setActivityData([]);
    } finally {
      setChildLoading(false);
    }
  };

  const handleViewHoldings = async (institution: Institution) => {
    setSelectedInstitution(institution);
    setCurrentView('holdings');
    setChildLoading(true);
    setChildSearchTerm('');
    setChildCurrentPage(1);
    setChildSortField('value');
    try {
      const data = await apiService.getInstitutionHoldings(institution.name, { limit: 500 });
      setHoldingsData(data);
    } catch (error) {
      console.error('Failed to load holdings:', error);
      setHoldingsData([]);
    } finally {
      setChildLoading(false);
    }
  };

  const handleViewSectors = async (institution: Institution) => {
    setSelectedInstitution(institution);
    setCurrentView('sectors');
    setChildLoading(true);
    setChildSearchTerm('');
    setChildCurrentPage(1);
    setChildSortField('value');
    try {
      const data = await apiService.getInstitutionSectors(institution.name);
      setSectorsData(data);
    } catch (error) {
      console.error('Failed to load sectors:', error);
      setSectorsData([]);
    } finally {
      setChildLoading(false);
    }
  };

  const handleBackToList = () => {
    setCurrentView('list');
    setSelectedInstitution(null);
    setActivityData([]);
    setHoldingsData([]);
    setSectorsData([]);
    setChildSearchTerm('');
    setChildCurrentPage(1);
  };

  const handleChildSort = (field: string) => {
    if (childSortField === field) {
      setChildSortDirection(childSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setChildSortField(field);
      setChildSortDirection('desc');
    }
  };

  // Generic child data filtering and sorting
  const filterAndSortChildData = <T extends Record<string, any>>(data: T[], searchFields: string[]): T[] => {
    let filtered = data;
    
    // Search filter
    if (childSearchTerm) {
      const search = childSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        searchFields.some(field => {
          const value = item[field];
          if (value === null || value === undefined) return false;
          return String(value).toLowerCase().includes(search);
        })
      );
    }
    
    // Sorting
    if (childSortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[childSortField];
        let bValue = b[childSortField];
        
        if (aValue === null || aValue === undefined) aValue = '';
        if (bValue === null || bValue === undefined) bValue = '';
        
        // Handle numeric fields
        if (typeof aValue === 'number' || !isNaN(parseFloat(aValue))) {
          aValue = parseFloat(aValue) || 0;
          bValue = parseFloat(bValue) || 0;
        } else if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = typeof bValue === 'string' ? bValue.toLowerCase() : '';
        }
        
        if (aValue < bValue) return childSortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return childSortDirection === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  };

  // Render pagination controls
  const renderPagination = (totalItems: number, currentPage: number, onPageChange: (page: number) => void) => {
    const totalPages = Math.ceil(totalItems / childItemsPerPage);
    const startIndex = (currentPage - 1) * childItemsPerPage;
    const endIndex = Math.min(startIndex + childItemsPerPage, totalItems);
    
    if (totalItems <= childItemsPerPage) {
      return (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>Showing all {totalItems} results</span>
          </div>
        </div>
      );
    }
    
    return (
      <div className="pagination-container">
        <div className="pagination-info">
          <span>Showing {startIndex + 1} to {endIndex} of {totalItems} results</span>
        </div>
        <div className="pagination-controls">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          <div className="pagination-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => onPageChange(pageNum)}
                  className={`pagination-number ${currentPage === pageNum ? 'pagination-number-active' : 'pagination-number-inactive'}`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  // Render child views
  if (currentView === 'activity' && selectedInstitution) {
    const filteredData = filterAndSortChildData(activityData, ['ticker', 'security_type', 'report_date']);
    const startIndex = (childCurrentPage - 1) * childItemsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + childItemsPerPage);

    return (
      <div className="component-container">
        <div className="page-header">
          <button onClick={handleBackToList} className="back-button">
            ← Back to Institutions
          </button>
          <h2 className="page-title">Activity - {selectedInstitution.short_name || selectedInstitution.name}</h2>
        </div>

        {/* Search Bar */}
        <div className="search-filter-container">
          <div className="search-container">
            <div className="search-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search activity..."
              value={childSearchTerm}
              onChange={(e) => {
                setChildSearchTerm(e.target.value);
                setChildCurrentPage(1);
              }}
              className="search-input"
            />
            {childSearchTerm && (
              <button onClick={() => setChildSearchTerm('')} className="search-clear">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}

        {childLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading activity data...</p>
          </div>
        ) : (
          <>
            {/* Top Scrollbar - Activity */}
            <div ref={activityTopScrollRef} className="top-scrollbar">
              <div ref={activityTopScrollContentRef} className="top-scrollbar-content"></div>
            </div>

            <div ref={activityTableContainerRef} className="table-container">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell" onClick={() => handleChildSort('units_change')}>
                      <div className="table-header-content">
                        Action {getSortIcon('units_change', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('ticker')}>
                    <div className="table-header-content">
                      Ticker {getSortIcon('ticker', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('security_type')}>
                    <div className="table-header-content">
                      Security Type {getSortIcon('security_type', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('units')}>
                    <div className="table-header-content">
                      Units {getSortIcon('units', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('units_change')}>
                    <div className="table-header-content">
                      Units Change {getSortIcon('units_change', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('buy_price')}>
                    <div className="table-header-content">
                      Buy Price {getSortIcon('buy_price', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('sell_price')}>
                    <div className="table-header-content">
                      Sell Price {getSortIcon('sell_price', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('avg_price')}>
                    <div className="table-header-content">
                      Avg Price {getSortIcon('avg_price', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('price_on_filing')}>
                    <div className="table-header-content">
                      Price on Filing {getSortIcon('price_on_filing', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('report_date')}>
                    <div className="table-header-content">
                      Report Date {getSortIcon('report_date', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('filing_date')}>
                    <div className="table-header-content">
                      Filing Date {getSortIcon('filing_date', childSortField, childSortDirection)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {paginatedData.map((activity, index) => {
                  const isBuy = activity.units_change > 0;
                  const isSell = activity.units_change < 0;
                  return (
                    <tr key={index} className={`table-row ${isBuy ? 'activity-buy-row' : isSell ? 'activity-sell-row' : ''}`}>
                      <td className="table-cell table-cell-primary child-cell">
                        {isBuy && <span className="activity-badge buy-badge">BOUGHT</span>}
                        {isSell && <span className="activity-badge sell-badge">SOLD</span>}
                        {!isBuy && !isSell && <span className="activity-badge neutral-badge">NO CHANGE</span>}
                      </td>
                      <td className="table-cell table-cell-primary child-cell">
                        <span className="ticker-symbol">{activity.ticker}</span>
                      </td>
                      <td className="table-cell child-cell">{activity.security_type}</td>
                      <td className="table-cell child-cell">{activity.units.toLocaleString()}</td>
                      <td className="table-cell child-cell">
                        <div className={`units-change-cell ${isBuy ? 'buy-change' : isSell ? 'sell-change' : ''}`}>
                          {isBuy && <span className="change-icon">📈</span>}
                          {isSell && <span className="change-icon">📉</span>}
                          <span className="change-value">
                            {activity.units_change > 0 ? '+' : ''}{activity.units_change.toLocaleString()}
                          </span>
                        </div>
                      </td>
                      <td className="table-cell child-cell">{activity.buy_price ? `$${activity.buy_price}` : '-'}</td>
                      <td className="table-cell child-cell">{activity.sell_price ? `$${activity.sell_price}` : '-'}</td>
                      <td className="table-cell child-cell">{activity.avg_price ? `$${activity.avg_price}` : 'N/A'}</td>
                      <td className="table-cell child-cell">${activity.price_on_filing}</td>
                      <td className="table-cell table-cell-secondary child-cell">{activity.report_date}</td>
                      <td className="table-cell table-cell-secondary child-cell">{activity.filing_date}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {paginatedData.length === 0 && (
              <div className="empty-state">No activity data available</div>
            )}
          </div>
          </>
        )}

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}
      </div>
    );
  }

  if (currentView === 'holdings' && selectedInstitution) {
    const filteredData = filterAndSortChildData(holdingsData, ['ticker', 'full_name', 'sector', 'security_type']);
    const startIndex = (childCurrentPage - 1) * childItemsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + childItemsPerPage);

    return (
      <div className="component-container">
        <div className="page-header">
          <button onClick={handleBackToList} className="back-button">
            ← Back to Institutions
          </button>
          <h2 className="page-title">Holdings - {selectedInstitution.short_name || selectedInstitution.name}</h2>
        </div>

        {/* Search Bar */}
        <div className="search-filter-container">
          <div className="search-container">
            <div className="search-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search holdings..."
              value={childSearchTerm}
              onChange={(e) => {
                setChildSearchTerm(e.target.value);
                setChildCurrentPage(1);
              }}
              className="search-input"
            />
            {childSearchTerm && (
              <button onClick={() => setChildSearchTerm('')} className="search-clear">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}

        {childLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading holdings data...</p>
          </div>
        ) : (
          <>
            {/* Top Scrollbar - Holdings */}
            <div ref={holdingsTopScrollRef} className="top-scrollbar">
              <div ref={holdingsTopScrollContentRef} className="top-scrollbar-content"></div>
            </div>

            <div ref={holdingsTableContainerRef} className="table-container">
              <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell" onClick={() => handleChildSort('ticker')}>
                    <div className="table-header-content">
                      Ticker {getSortIcon('ticker', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('full_name')}>
                    <div className="table-header-content">
                      Company {getSortIcon('full_name', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('sector')}>
                    <div className="table-header-content">
                      Sector {getSortIcon('sector', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('security_type')}>
                    <div className="table-header-content">
                      Type {getSortIcon('security_type', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('units')}>
                    <div className="table-header-content">
                      Units {getSortIcon('units', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('value')}>
                    <div className="table-header-content">
                      Value {getSortIcon('value', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('avg_price')}>
                    <div className="table-header-content">
                      Avg Price {getSortIcon('avg_price', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('close')}>
                    <div className="table-header-content">
                      Close Price {getSortIcon('close', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('units_change')}>
                    <div className="table-header-content">
                      Units Change {getSortIcon('units_change', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('perc_of_total')}>
                    <div className="table-header-content">
                      % of Total {getSortIcon('perc_of_total', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('change_perc')}>
                    <div className="table-header-content">
                      Change % {getSortIcon('change_perc', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('date')}>
                    <div className="table-header-content">
                      Date {getSortIcon('date', childSortField, childSortDirection)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {paginatedData.map((holding, index) => (
                  <tr key={index} className="table-row">
                    <td className="table-cell table-cell-primary child-cell">{holding.ticker}</td>
                    <td className="table-cell child-cell">{holding.full_name}</td>
                    <td className="table-cell child-cell">{holding.sector}</td>
                    <td className="table-cell child-cell">{holding.security_type}</td>
                    <td className="table-cell child-cell">{holding.units.toLocaleString()}</td>
                    <td className="table-cell table-cell-primary child-cell">{formatValue(holding.value)}</td>
                    <td className="table-cell child-cell">${holding.avg_price}</td>
                    <td className="table-cell child-cell">${holding.close}</td>
                    <td className={`table-cell child-cell ${holding.units_change > 0 ? 'text-green' : holding.units_change < 0 ? 'text-red' : ''}`}>
                      {holding.units_change > 0 ? '+' : ''}{holding.units_change.toLocaleString()}
                    </td>
                    <td className="table-cell child-cell">{formatPercent(holding.perc_of_total)}</td>
                    <td className={`table-cell child-cell ${parseFloat(holding.change_perc) > 0 ? 'text-green' : parseFloat(holding.change_perc) < 0 ? 'text-red' : ''}`}>
                      {formatPercent(holding.change_perc)}
                    </td>
                    <td className="table-cell table-cell-secondary child-cell">{holding.date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginatedData.length === 0 && (
              <div className="empty-state">No holdings data available</div>
            )}
          </div>
          </>
        )}

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}
      </div>
    );
  }

  if (currentView === 'sectors' && selectedInstitution) {
    const filteredData = filterAndSortChildData(sectorsData, ['sector']);
    const startIndex = (childCurrentPage - 1) * childItemsPerPage;
    const paginatedData = filteredData.slice(startIndex, startIndex + childItemsPerPage);

    return (
      <div className="component-container">
        <div className="page-header">
          <button onClick={handleBackToList} className="back-button">
            ← Back to Institutions
          </button>
          <h2 className="page-title">Sectors - {selectedInstitution.short_name || selectedInstitution.name}</h2>
        </div>

        {/* Search Bar */}
        <div className="search-filter-container">
          <div className="search-container">
            <div className="search-icon">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              type="text"
              placeholder="Search sectors..."
              value={childSearchTerm}
              onChange={(e) => {
                setChildSearchTerm(e.target.value);
                setChildCurrentPage(1);
              }}
              className="search-input"
            />
            {childSearchTerm && (
              <button onClick={() => setChildSearchTerm('')} className="search-clear">
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}

        {childLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading sectors data...</p>
          </div>
        ) : (
          <>
            {/* Top Scrollbar - Sectors */}
            <div ref={sectorsTopScrollRef} className="top-scrollbar">
              <div ref={sectorsTopScrollContentRef} className="top-scrollbar-content"></div>
            </div>

            <div ref={sectorsTableContainerRef} className="table-container">
              <table className="table">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell" onClick={() => handleChildSort('sector')}>
                      <div className="table-header-content">
                        Sector {getSortIcon('sector', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('value')}>
                    <div className="table-header-content">
                      Value {getSortIcon('value', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('positions')}>
                    <div className="table-header-content">
                      Positions {getSortIcon('positions', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('positions_increased')}>
                    <div className="table-header-content">
                      Increased {getSortIcon('positions_increased', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('positions_decreased')}>
                    <div className="table-header-content">
                      Decreased {getSortIcon('positions_decreased', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('positions_closed')}>
                    <div className="table-header-content">
                      Closed {getSortIcon('positions_closed', childSortField, childSortDirection)}
                    </div>
                  </th>
                  <th className="table-header-cell" onClick={() => handleChildSort('report_date')}>
                    <div className="table-header-content">
                      Report Date {getSortIcon('report_date', childSortField, childSortDirection)}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="table-body">
                {paginatedData.map((sector, index) => (
                  <tr key={index} className="table-row">
                    <td className="table-cell table-cell-primary child-cell">{sector.sector}</td>
                    <td className="table-cell table-cell-primary child-cell">{formatValue(sector.value)}</td>
                    <td className="table-cell child-cell">{sector.positions}</td>
                    <td className="table-cell text-green child-cell">{sector.positions_increased}</td>
                    <td className="table-cell text-red child-cell">{sector.positions_decreased}</td>
                    <td className="table-cell text-red child-cell">{sector.positions_closed}</td>
                    <td className="table-cell table-cell-secondary child-cell">{sector.report_date}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {paginatedData.length === 0 && (
              <div className="empty-state">No sectors data available</div>
            )}
          </div>
          </>
        )}

        {renderPagination(filteredData.length, childCurrentPage, setChildCurrentPage)}
      </div>
    );
  }

  // Main list view
  return (
    <div className="component-container">
      <div className="page-header">
        <h2 className="page-title">Institutions</h2>
        <p className="page-subtitle">Hedge funds, investment firms, and institutional investors</p>
      </div>

      {/* Search and Filter Controls */}
      <div className="search-filter-container">
        {/* Search Input */}
        <div className="search-container">
          <div className="search-icon">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            placeholder="Search by name, CIK..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="search-input"
          />
          {searchTerm && (
            <button onClick={() => setSearchTerm('')} className="search-clear">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Filter Controls */}
        <div className="filter-controls-row">
          <div className="filter-buttons">
            <button
              onClick={() => setHedgeFundFilter('all')}
              className={`filter-button ${hedgeFundFilter === 'all' ? 'filter-button-active' : 'filter-button-inactive'}`}
            >
              All Institutions
            </button>
            <button
              onClick={() => setHedgeFundFilter('hedge_fund')}
              className={`filter-button ${hedgeFundFilter === 'hedge_fund' ? 'filter-button-active' : 'filter-button-inactive'}`}
            >
              Hedge Funds
            </button>
            <button
              onClick={() => setHedgeFundFilter('other')}
              className={`filter-button ${hedgeFundFilter === 'other' ? 'filter-button-active' : 'filter-button-inactive'}`}
            >
              Other Institutions
            </button>
          </div>

          {/* Value Range Filter */}
          <div className="value-filter-group">
            <input
              type="number"
              placeholder="Min Value"
              value={minTotalValue}
              onChange={(e) => {
                setMinTotalValue(e.target.value);
                setCurrentPage(1);
              }}
              className="value-filter-input"
            />
            <input
              type="number"
              placeholder="Max Value"
              value={maxTotalValue}
              onChange={(e) => {
                setMaxTotalValue(e.target.value);
                setCurrentPage(1);
              }}
              className="value-filter-input"
            />
          </div>
        </div>

        {/* Results Count */}
        {searchTerm && (
          <div className="search-results-count">
            Showing {startIndex + 1}-{Math.min(endIndex, sortedInstitutions.length)} of {sortedInstitutions.length} results
          </div>
        )}
      </div>

      {/* Pagination - Top */}
      {sortedInstitutions.length > itemsPerPage && (
        <div className="pagination-container pagination-top">
          <div className="pagination-info">
            <span>
              Showing {startIndex + 1} to {Math.min(endIndex, sortedInstitutions.length)} of {sortedInstitutions.length} results
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <div className="pagination-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-number ${currentPage === pageNum ? 'pagination-number-active' : 'pagination-number-inactive'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Institutions Table */}
      {loading ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading institutions...</p>
        </div>
      ) : (
        <>
          {/* Top Scrollbar - Synced with table */}
          <div ref={topScrollRef} className="top-scrollbar">
            <div ref={topScrollContentRef} className="top-scrollbar-content"></div>
          </div>

          <div ref={tableContainerRef} className="table-container">
            <table className="table institutions-table">
            <thead className="table-header">
              <tr>
                <th className="table-header-cell" onClick={() => handleSort('name')}>
                  <div className="table-header-content">
                    Institution {getSortIcon('name', sortField, sortDirection)}
                  </div>
                </th>
                <th className="table-header-cell">Type</th>
                <th className="table-header-cell" onClick={() => handleSort('total_value')}>
                  <div className="table-header-content">
                    Total Value {getSortIcon('total_value', sortField, sortDirection)}
                  </div>
                </th>
                <th className="table-header-cell" onClick={() => handleSort('share_value')}>
                  <div className="table-header-content">
                    Share Holdings {getSortIcon('share_value', sortField, sortDirection)}
                  </div>
                </th>
                <th className="table-header-cell">Actions</th>
                <th className="table-header-cell">Fund Holdings</th>
                <th className="table-header-cell">Options Value</th>
                <th className="table-header-cell">Buy/Sell Activity</th>
                <th className="table-header-cell">CIK</th>
                <th className="table-header-cell" onClick={() => handleSort('filing_date')}>
                  <div className="table-header-content">
                    Filing Date {getSortIcon('filing_date', sortField, sortDirection)}
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="table-body">
              {paginatedInstitutions.map((institution, index) => (
                <tr key={index} className="table-row institution-row">
                  <td className="table-cell">
                    <div className="institution-name-cell">
                      <div className="institution-name-main">
                        <span className="institution-name">{institution.short_name || institution.name}</span>
                        {institution.tags && institution.tags.length > 0 && (
                          <div className="institution-tags">
                            {institution.tags.map((tag, i) => (
                              <span key={i} className="institution-tag">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      {institution.short_name && institution.short_name !== institution.name && (
                        <div className="institution-full-name">{institution.name}</div>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    {institution.is_hedge_fund ? (
                      <span className="type-badge hedge-fund">🏦 Hedge Fund</span>
                    ) : (
                      <span className="type-badge institution">🏢 Institution</span>
                    )}
                  </td>
                  <td className="table-cell table-cell-primary">
                    <div className="value-cell-highlight">
                      {formatValue(institution.total_value)}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="holdings-breakdown">
                      <div className="holdings-value">{formatValue(institution.share_value)}</div>
                      <div className="holdings-count">{parseFloat(institution.share_holdings || '0').toLocaleString()} shares</div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="action-buttons">
                      <button
                        onClick={() => handleViewHoldings(institution)}
                        className="action-button holdings-btn"
                        title="View Holdings"
                      >
                        <span className="btn-icon">📊</span> Holdings
                      </button>
                      <button
                        onClick={() => handleViewSectors(institution)}
                        className="action-button sectors-btn"
                        title="View Sectors"
                      >
                        <span className="btn-icon">🎯</span> Sectors
                      </button>
                      <button
                        onClick={() => handleViewActivity(institution)}
                        className="action-button activity-btn"
                        title="View Activity"
                      >
                        <span className="btn-icon">⚡</span> Activity
                      </button>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="holdings-breakdown">
                      <div className="holdings-value">{formatValue(institution.fund_value)}</div>
                      <div className="holdings-count">{parseFloat(institution.fund_holdings || '0').toLocaleString()} funds</div>
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="options-breakdown">
                      {(parseFloat(institution.call_value || '0') > 0 || parseFloat(institution.put_value || '0') > 0) ? (
                        <>
                          <div className="options-line">
                            <span className="options-label call-label">Calls:</span>
                            <span className="options-value">{formatValue(institution.call_value)}</span>
                          </div>
                          <div className="options-line">
                            <span className="options-label put-label">Puts:</span>
                            <span className="options-value">{formatValue(institution.put_value)}</span>
                          </div>
                        </>
                      ) : (
                        <span className="no-data">No Options</span>
                      )}
                    </div>
                  </td>
                  <td className="table-cell">
                    <div className="buy-sell-indicator">
                      <div className="buy-sell-line">
                        <span className="activity-icon buy-icon">📈</span>
                        <span className="buy-value">{formatValue(institution.buy_value)}</span>
                      </div>
                      <div className="buy-sell-line">
                        <span className="activity-icon sell-icon">📉</span>
                        <span className="sell-value">{formatValue(institution.sell_value)}</span>
                      </div>
                    </div>
                  </td>
                  <td className="table-cell table-cell-secondary">
                    <div className="cik-cell">{institution.cik}</div>
                  </td>
                  <td className="table-cell table-cell-secondary">
                    <div className="date-cell">
                      <div className="filing-date">{institution.filing_date}</div>
                      <div className="report-date">Report: {institution.date}</div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {institutions.length === 0 && !loading && (
        <div className="empty-state">No institutions data available</div>
      )}

      {/* Pagination - Bottom */}
      {sortedInstitutions.length > itemsPerPage && (
        <div className="pagination-container">
          <div className="pagination-info">
            <span>
              Showing {startIndex + 1} to {Math.min(endIndex, sortedInstitutions.length)} of {sortedInstitutions.length} results
            </span>
          </div>
          <div className="pagination-controls">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <div className="pagination-numbers">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum: number;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`pagination-number ${currentPage === pageNum ? 'pagination-number-active' : 'pagination-number-inactive'}`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
